import type {
    SelectMenuGroup,
    SelectMenuItem,
} from '@local/web-design-system/components/SelectMenu';

import type {
    EvoCentralInstance,
    EvoCentralInstances,
} from '../../apiClients/discoveryClient/requests/Discovery.types';
import type {
    CentralInstance,
    CentralInstances,
} from '../../apiClients/entitlementsClient/requests/Entitlements.types';
import type { Discovery } from '../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import {
    type EvoInstance,
    getAvailableEvoInstances,
    getCurrentEvoInstance,
} from '../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { getSelectedInstanceState } from '../../store/sessionStorageHelpers/selectedInstanceHelper';

export function optionsWithEvo(
    data?: CentralInstances,
    evoData?: EvoCentralInstances,
): SelectMenuItem[] {
    const allInstances = getAllInstances(data?.servers, evoData?.instances);
    return allInstances
        .map((e) => ({
            key: e.instanceUUID,
            label: e.evoConnected ? `${e.displayName} (Evo)` : e.displayName,
        }))
        .sort() as SelectMenuItem[];
}

export function optionGroups(instances: CentralInstance[]): SelectMenuGroup[] {
    const response: any = [];
    instances
        .map((e) => e.organizationName)
        .reduce((previousValue: any, currentValue: any) => {
            if (previousValue.indexOf(currentValue) === -1) {
                previousValue.push(currentValue);
            }
            return previousValue;
        }, [])
        .sort((a: string, b: string) => (a.toLocaleLowerCase() < b.toLocaleLowerCase() ? -1 : 0))
        .forEach((org: any) => {
            const instanceList = instances
                .filter((e) => e.organizationName === org)
                .sort((a, b) => (a.host > b.host ? 1 : -1))
                .map((e) => `${e.instanceUUID}`);
            response.push({ label: org, optionIds: instanceList });
        });
    return response;
}

export function getPreviousOrDefaultInstance(
    data?: CentralInstances,
    evoData?: EvoCentralInstances,
) {
    const allInstances = getAllInstances(data?.servers, evoData?.instances);
    if (allInstances.length === 0) {
        return '';
    }

    // Previous instance
    const filteredInstances = allInstances.find(
        (server) => server.host === getSelectedInstanceState(),
    );
    if (filteredInstances) {
        const { instanceUUID } = filteredInstances;
        return instanceUUID;
    }

    // Default (first) instance
    return optionGroupsWithEvo(data, evoData)[0]?.optionIds[0];
}

export function getPreviousOrDefaultOrg(): string {
    const previousInstance = getCurrentEvoInstance();
    if (previousInstance && previousInstance.org) {
        return previousInstance.org.id;
    }

    const allEvo = getAvailableEvoInstances();
    if (!allEvo || allEvo.length === 0) {
        return '';
    }
    // Default (first) instance
    return allEvo[0].org.id;
}

export const getEvoInstanceForOrgIdWithOptionalHubCode = (
    orgId: string,
    evoData?: Discovery,
    hubCode?: string,
): EvoInstance | null => {
    if (!evoData) {
        return null;
    }

    const org = evoData?.organizations.find((o) => o.id === orgId);
    const serviceAccess = hubCode
        ? evoData?.service_access.find((o) => o.org_id === orgId && o.hub_code === hubCode)
        : evoData?.service_access.find((o) => o.org_id === orgId);
    const hub = evoData?.hubs.find((h) => h.code === serviceAccess?.hub_code);

    if (!hub || !org) {
        return null;
    }
    return { org, hub };
};

export function optionGroupsWithEvo(
    data?: CentralInstances,
    evoData?: EvoCentralInstances,
): SelectMenuGroup[] {
    const allInstances = getAllInstances(data?.servers, evoData?.instances);
    return [...optionGroups(allInstances)];
}

export function getAllInstances(
    entitlementInstances: CentralInstance[] = [],
    discoveryInstances: EvoCentralInstance[] = [],
): (CentralInstance & { evoConnected: boolean })[] {
    const discoveryIds = new Set(discoveryInstances.map((i) => i.centralInstanceUUID));
    const entitlementIds = new Set(entitlementInstances.map((i) => i.instanceUUID));
    const evoConnectedInstances = entitlementInstances.filter((server) =>
        discoveryIds.has(server.instanceUUID),
    );
    const centralOnlyInstances = entitlementInstances.filter(
        (server) => !discoveryIds.has(server.instanceUUID),
    );
    const evoOnlyInstances = discoveryInstances.filter(
        (server) => !entitlementIds.has(server.centralInstanceUUID),
    );

    return [
        // Append any instances from the discovery service that are not coming from Idp
        ...evoOnlyInstances.map((instance) => ({
            ...toCentralInstance(instance),
            evoConnected: true,
        })),
        ...evoConnectedInstances.map((instance) => ({ ...instance, evoConnected: true })),
        ...centralOnlyInstances.map((instance) => ({ ...instance, evoConnected: false })),
    ];
}

function toCentralInstance(instance: EvoCentralInstance): CentralInstance {
    return {
        displayName: instance.centralDisplayName,
        host: instance.centralHost,
        port: '443',
        instanceUUID: instance.centralInstanceUUID,
        // This will become an issue once Evo connected instances are no longer served by the Entitlements API,
        // and are returned exclusively by the Evo Discovery Service
        entitlementId: '',
        name: instance.centralName,
        organizationName: instance.centralOrganizationName,
        validTo: '',
    };
}
